import React, { useCallback, useEffect, useState } from 'react';
import { FaSearch } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import apiReport from 'services/apis/report';

import ApplicationLayout from 'v3/layouts/Application';
import ExpiredDocumentsFilter from './Filter';
import useQuery from 'hooks/useQuery';
import { formatDateTime } from 'v3/utils/formatter';
import { Row } from 'react-bootstrap';

import {
  DataTable,
  Text,
  Space,
  Button,
} from 'v3/components';
import { useSnackbar } from 'v3/components/Snackbar';
import { Attachment } from './Attachment';

import { mediaTypeOptions } from './controller'
import { usePermission } from 'hooks';

export function ExpiredDocuments() {

  usePermission('VISUALIZAR_RELATORIO_DOCUMENTOS_VENCIDOS', {
    redirect: true,
  });

  const queryParams = useQuery();
  const snackbar = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [data, setData] = useState([]);

  const [showAdvancedFiltersModal, setShowAdvancedFiltersModal] = useState(false);
  const [qtdAdvancedFilters, setQtdAdvancedFilters] = useState(0);
  const [attachment, setAttachment] = useState(null);

  const [pagination, setPagination] = useState({
    total: 0,
    lastPage: 1,
    currentPage: 1,
  });

  const [advancedFilters, setAdvancedFilters] = useState({
    drivers: [],
    vehicles: [],
    wagons: [],
    end_date: null,
    start_date: null,
    entity_type: null,
    media_type: null,
    page: 1,
    per_page: 10
  });

  const formatTypeOptions = (type) => {

    const typeOption = mediaTypeOptions().find((option) => option.slug === type);

    return typeOption?.name || '-';
  }

  const formatEntytyidentifierRow = (row) => {

    if (row?.entity_type === 'PERSON') {
      return <Link
        to={`/motoristas/${row?.entity_id}`}
        target="_blank"
      >
        {row?.entity_identifier}
      </Link>
    } else if (row?.entity_type === 'VEHICLE') {
      return <Link
        to={`/veiculos/${row?.entity_id}`}
        target="_blank"
      >
        {row?.entity_identifier}
      </Link>

    } else if (row?.entity_type === 'WAGON') {
      return <Link
        to={`/implementos/${row?.entity_id}`}
        target="_blank"
      >
        {row?.entity_identifier}
      </Link>

    } else {
      return '-'
    }
  }

  const entityTypeRow = (entity_type) => {
    switch (entity_type) {
      case 'PERSON':
        return 'Motorista';
      case 'VEHICLE':
        return 'Veículo';
      case 'WAGON':
        return 'Implemento';
      default:
        return '-';
    }
  }

  const fetchData = useCallback(async (page = 1, per_page = 10) => {

    setLoading(true);

    try {

      const response = await apiReport.get(`expired-documents?page=${page}&per_page=${per_page}&${queryParams}`);
      const { data, ...rest } = response.data;

      setPagination(rest?.pagination);
      setData(data);

    } catch (error) {
      setData([]);
      const message =
        error?.response?.data?.error ||
        'Ocorreu um erro. Tente novamente mais tarde';
      snackbar.show(<Text>{`${message}`}</Text>, { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  async function changePage(page) {
    setAdvancedFilters({ ...advancedFilters, page: page });
    fetchData(page, advancedFilters?.per_page);
  }

  async function changePerPage(per_page) {
    setAdvancedFilters({ ...advancedFilters, per_page: per_page });
    fetchData(advancedFilters?.page, per_page);
  }

  useEffect(() => {
    fetchData();
  }, []);

  const opemAttachment = async (row) => {

    setAttachment({
      ...row,
    });
  }

  return (
    <ApplicationLayout
      title={"Relatório de vencimento de documentos"}
      RightComponent={
        <Space x="0.25rem" y="0.25rem">
          <Button
            variant="secondary"
            onClick={() => setShowAdvancedFiltersModal(true)}
            disabled={loading}
          >
            <Text
              weight={500}
            >{`Filtro Avançado (${qtdAdvancedFilters})`}</Text>
          </Button>
        </Space>
      }
    >
      <Row style={{ marginBottom: '3rem' }}>
        <ExpiredDocumentsFilter
          loading={loading}
          onChange={fetchData}
          showAdvancedFiltersModal={showAdvancedFiltersModal}
          setShowAdvancedFiltersModal={setShowAdvancedFiltersModal}
          setQtdAdvancedFilters={setQtdAdvancedFilters}
          queryParams={queryParams}
          setAdvancedFilters={setAdvancedFilters}
          advancedFilters={advancedFilters}
        />
      </Row>
      <DataTable
        data={data}
        loading={loading}
        withFilterOption={false}
        tableStyles={{
          borderCollapse: 'collapse',
          '> thead > tr > th': {
            borderBottom: '2px solid #c5ccd5',
          },
          '> tbody > tr': {
            borderBottom: '1px solid #d8dee6',
          },
          '> tbody > tr:hover': {
            background: '#f3f6fa',
          },
          '> tbody > tr:hover > td': {
            background: '#f3f6fa',
          },
        }}
        page={pagination?.currentPage}
        pages={pagination?.lastPage}
        total={pagination?.total}
        onPageChanged={changePage}
        onPerPageChanged={changePerPage}
        defaultPerPage={10}
        perPageOptions={[{ v: 10 }, { v: 20 }, { v: 30 }, { v: 50 }]}
        columns={[
          {
            name: 'Tipos de registro',
            selector: row => entityTypeRow(row?.entity_type),
          },
          {
            name: 'Motorista/Placa',
            selector: row => formatEntytyidentifierRow(row) || '-',
          },
          {
            name: 'Data de Vencimento',
            selector: row => formatDateTime(row?.media_due_date) || '-',
          },
          {
            name: 'Tipo de anexo',
            selector: row => formatTypeOptions(row?.media_type),
          },
          {
            name: 'Nome do arquivo',
            selector: row => row?.media_name || '-',
          },
          {
            name: 'OBS',
            selector: row => row?.media_description || '-',
          },
          {
            name: 'Data inclusão',
            selector: row => formatDateTime(row?.media_created_at) || '-',
          },
          {
            name: 'Visualizar',
            selector: row =>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                < FaSearch
                  style={{ fontSize: '1.5rem', cursor: 'pointer' }}
                  onClick={() => opemAttachment(row)}
                />
              </div>
          }
        ]}
      />
      {
        attachment && (
          <Attachment
            attachment={attachment}
            attachments={[attachment]}
          />
        )
      }
    </ApplicationLayout>
  )
}
